import { createSelector } from '@reduxjs/toolkit';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { securitiesSelectors } from 'app-components/flux/securities';
import type RootState from 'app-components/utils/redux-store/rootState';

import { TICKER_LOGOS_REDUCER_KEY } from './tickerLogosTypes';

const tickerValidateDefault = /^[A-Z]{1,5}(\.[A-Z]{1,3})?$/;

type State = Pick<RootState, typeof TICKER_LOGOS_REDUCER_KEY>;

export const getLogosStore = (state: State) => state[TICKER_LOGOS_REDUCER_KEY];

export const getResourcesById = (state: State) => getLogosStore(state).resourcesById;
export const getLoadPending = (state: State) => getLogosStore(state).loadPending;
export const getIsLoading = (state: State) => getLogosStore(state).isLoading;
export const getError = (state: State) => getLogosStore(state).error;
export const getLastSyncDate = (state: State) => getLogosStore(state).lastSyncDate;

export const getTickerLogosByKey = createSelector([getResourcesById], (logos) =>
  logos.mapKeys((_id, logo) => logo.ticker),
);

export const getTickerLogoByKey = createSelector([getTickerLogosByKey, (_state, key) => key], (logosByKey, key) =>
  logosByKey.get(key),
);

export const getTickersMissing = createSelector(
  [
    getTickerLogosByKey,
    (_state, tickers) => tickers,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'tickerValidate'),
  ],
  (logosByKey, tickers, tickerValidate) =>
    tickers
      .filter((ticker: string) => ticker.match(tickerValidate ?? tickerValidateDefault))
      .filter((ticker: string) => !logosByKey.has(ticker)),
);

export const getTickersToAdd = createSelector(
  [
    getTickerLogosByKey,
    securitiesSelectors.getResourcesById,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'tickerValidate'),
  ],
  (logosByKey, securities, tickerValidate) =>
    securities
      ?.filter((security) => security.symbol?.match(tickerValidate ?? tickerValidateDefault))
      ?.filter((security) => security.symbol && !logosByKey.has(security.symbol))
      .map((security) => security.symbol)
      .toIndexedSeq()
      .toArray(),
);

export const getTickersToUpdate = createSelector(
  [
    getTickerLogosByKey,
    securitiesSelectors.getResourcesById,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'tickerValidate'),
  ],
  (logosByKey, securities, tickerValidate) => {
    return securities
      ?.filter((security) => security.symbol?.match(tickerValidate ?? tickerValidateDefault))
      .filter((security) => logosByKey.has(security.symbol))
      .map((security) => security.symbol || '')
      .toIndexedSeq()
      .toArray();
  },
);
